import { http } from './http-client'
import { actionCreatorsFor, actionCreatorWithParentKey } from './redux-crud-actions'
import { createNewRestErrorAction } from './redux-error'
import logger from './log'
import { DEFAULT_CONTEXT_PATH, FETCH_STATE_PENDING } from './constants'

/**
 * Usage Example:
 *
 * const actions = createCrudActions($resourceName, $contextPath)
 * actions.insert(($insertRequestBody))
 *        .then((result) => { // handle success response })
 *        .catch((error) => {
 *            error.reasons.then((errors) => { // handle error reasons })
 *        })
 * actions.update(($updateRequestBody))
 *        .then((result) => { // handle success response })
 *        .catch((error) => {
 *            error.reasons.then((errors) => { // handle error reasons })
 *        })
 *
 * Note: createCrudActions and createBoundCrudActions could return error response contains reasons as type of promise,
 * please use correspondingly!
 */

/**
 * Provides common redux thunk actions for standard 'restish' crud operations
 * @param {string} resourceName identifying the rest resource
 * @param {string} contextPath contextPath, so to say prefix to the hrefs returned by the API
 * @return {object} an object with appropriate fetch, insert, update and delete functions as its properties
 */
export function createCrudActions(resourceName, contextPath) {
    const baseActions = actionCreatorsFor(resourceName)
    return crudActionsGenerator(resourceName, baseActions, contextPath)
}

/**
 * Provides common redux thunk actions for subresource 'restish' crud operations
 * @param {string} resourceName identifying the rest resource
 * @param {string} parentResourceKey identifying the rest resource
 * @param {string} contextPath contextPath, so to say prefix to the hrefs returned by the API
 * @return {object} an object with appropriate fetch, insert, update and delete functions as its properties
 */
export function createSubResourceCrudActions(resourceName, parentResourceKey, contextPath) {
    const baseActions = actionCreatorWithParentKey(resourceName, parentResourceKey)
    return crudActionsGenerator(resourceName, baseActions, contextPath)
}

function crudActionsGenerator(resourceName, baseActions, contextPath = DEFAULT_CONTEXT_PATH) {
    const defaultAbortControllers = {}
    const forgeAbortControllerKey = (action, url) => action + url

    return {
        fetch,
        diff,
        insert,
        update,
        remove,
        removeArray,
        assign,
        unassign,
        partialUpdate
    }

    /**
     * Fetches data from the provided rest endpoint via provided HTTP method (default is GET).
     * The final URL is build by concatenating the contextPath with the href parameter.
     *
     * @param {string} href the href to be fetched
     * @param {object} [options] additional options
     * @param {object} [options.abortController=defaultAbortController] - instance of AbortController, used to cancel the request
     * @param {object} [options.fetchOptions=null] additional/overwriting options for the underlying fetch
     * method; see {@link http.get()}.
     * @param {object} [options.filter=null] an object, whose key/value pairs are used to build a filter url
     * @param {boolean} [options.forceUpdate=false] a boolean value, that forces the action to be performed
     *                                                  regardless if the resource has already been fetched
     * @param {function} [options.method=http.get] HTTP method to be used for fetch request (see {@link http}),
     *                                                 allowed methods are GET, POST, or PUT
     * @param {object} [options.payload=null] payload object when using HTTP POST or PUT method
     * @param {boolean} [options.resetState=true] a boolean value, that might be interpreted by appropriate reducers
     *                                                to determine if the response data should replace the state
     *                                                completely or if any existing state should just be updated.
     * @return {Thunk} a redux action thunk
     */
    function fetch(href, options) {
        return async function (dispatch, getState) {
            const {
                fetchOptions = {},
                filter,
                forceUpdate = false,
                method = 'get',
                payload,
                resetState = true
            } = {
                ...options
            }

            if (!(method === 'get' || method === 'post')) {
                throw new Error('Provided fetch method is not valid - must be either GET or POST')
            }

            const url = buildUrl(href, filter)
            const abortControllerKey = forgeAbortControllerKey('fetch', url)
            const abortController = options?.abortController ?? defaultAbortControllers[abortControllerKey]
            const { fetchState } = getState()

            if (!forceUpdate && fetchState?.[resourceName]?.[url] !== undefined) {
                logger.log(
                    `Url "${url}" for Resource "${resourceName}" has already been fetched and forceUpdate is false.`
                )
                return fetchState[resourceName][url]
            }

            if (
                fetchState?.[resourceName]?.[url] === FETCH_STATE_PENDING &&
                abortController instanceof AbortController
            ) {
                logger.log(`Cancelling pending http request to ${resourceName}#${url}.`)
                abortController.abort()
                if (defaultAbortControllers[abortControllerKey]) {
                    delete defaultAbortControllers[abortControllerKey]
                }
            }

            dispatch(baseActions.fetchStart(url))

            if (!(options?.abortController instanceof AbortController)) {
                fetchOptions.abortController = new AbortController()
                defaultAbortControllers[abortControllerKey] = fetchOptions.abortController
            } else {
                fetchOptions.abortController = options.abortController
            }

            try {
                let result
                switch (method) {
                    case 'get': {
                        result = await http.get(url, fetchOptions)
                        break
                    }
                    case 'post': {
                        result = await http.post(url, payload, fetchOptions)
                        break
                    }
                    default: {
                        throw new Error(`Invalid http method: ${method}`)
                    }
                }
                const { data } = result ?? {}
                dispatch(baseActions.fetchSuccess(data, url, resetState))
                return result
            } catch (error) {
                if (error.code !== DOMException.ABORT_ERR) {
                    dispatch(createErrorThunk(error, baseActions.fetchError(error, url)))
                }
            }
        }
    }

    /**
     * Differential get: fetches modified data from the provided rest endpoint via http get.
     * The final URL is build by concatenating the contextPath with the href parameter
     *
     * @param {string} href the href to be fetched
     * @param {number} modified the modified timestamp (pass the modified timestamp of the previous request or 0)
     * @param {object} params key/value query-params pairs
     * @param options additional/overwriting options for the underlying fetch method
     * @property options.abortController - an AbortController whose signal is added to the fetch options to make the request abortable
     * @return {function} a thunk that returns a promise, which resolves to an object with the keys 'time' (youngest
     *         modification time of the result) and 'data' (the retrieved json data)
     */
    function diff(href, modified, params, options) {
        const url = buildUrl(href, params)
        return async function (dispatch) {
            dispatch(baseActions.patchStart())
            try {
                const result = await http.diff(url, modified, options)
                const { data } = result ?? {}
                dispatch(baseActions.patchSuccess(data))
                return result
            } catch (error) {
                dispatch(createErrorThunk(error, baseActions.patchError(error)))
            }
        }
    }

    /**
     * Posts the provided json to the rest endpoint, which usually means a create operation
     * The final URL is build by concatenating the contextPath with the href parameter
     * @param {string} href the resource href
     * @param {object} json the data in JSON format
     * @param {object} params key/value query-params pairs
     * @param {object} options additional/overwriting options for the underlying fetch method
     * @property options.abortController - an AbortController whose signal is added to the fetch options to make the request abortable
     * @return {function} a redux thunk action
     */
    function insert(href, json, params, options) {
        const url = buildUrl(href, params)
        return async function (dispatch) {
            dispatch(baseActions.createStart())
            try {
                const result = await http.post(url, json, options)
                const { data } = result ?? {}
                dispatch(baseActions.createSuccess(data))
                return result
            } catch (error) {
                dispatch(createErrorThunk(error, baseActions.createError(error)))
            }
        }
    }

    /**
     * Puts the provided json to the rest endpoint, which usually means an update operation
     * The final URL is build by concatenating the contextPath with the href parameter
     * @param {string} href the resource href
     * @param {object} json the data in JSON format
     * @param {object} params key/value query-params pairs
     * @param {object} options additional/overwriting options for the underlying fetch method
     * @property options.abortController - an AbortController whose signal is added to the fetch options to make the request abortable
     * @return {function} a redux thunk action
     */
    function update(href, json, params, options) {
        const url = buildUrl(href, params)
        return async function (dispatch) {
            dispatch(baseActions.updateStart())
            try {
                const result = await http.put(url, json, options)
                const { data } = result ?? {}
                dispatch(baseActions.updateSuccess(data))
                return result
            } catch (error) {
                dispatch(createErrorThunk(error, baseActions.updateError(error)))
            }
        }
    }

    /**
     * Sends a delete request to the rest endpoint
     * The final URL is build by concatenating the contextPath with the href parameter
     * @param {string} href the resource href
     * @param {object} json the data in JSON format
     * @param {object} params key/value query-params pairs
     * @param {object} options additional/overwriting options for the underlying fetch method
     * @property options.abortController - an AbortController whose signal is added to the fetch options to make the request abortable
     * @return {function} a redux thunk action
     */
    function remove(href, json, params, options) {
        const url = buildUrl(href, params)
        return async function (dispatch) {
            dispatch(baseActions.deleteStart())
            try {
                const result = await http.del(url, options)
                // The result body should be empty on deletions
                // dispatch deleteSuccess with json from input, because the response will not contain a body
                dispatch(baseActions.deleteSuccess(json))
                return result
            } catch (error) {
                dispatch(createErrorThunk(error, baseActions.deleteError(error)))
            }
        }
    }

    /**
     * Implements a delete operation via http post. The rest endpoint must provide delete semantics for the provided
     * json data. This is meant for bulk deletes (like unassign objects from groups or the like), because normal
     * http delete requests do not contain a message body.
     * The final URL is build by concatenating the contextPath with the href parameter
     * @param {string} href the resource href
     * @param {array} arr the data in JSON format
     * @param {object} params key/value query-params pairs
     * @param {object} options additional/overwriting options for the underlying fetch method
     * @property options.abortController - an AbortController whose signal is added to the fetch options to make the request abortable
     * @return {function} a redux thunk action
     */
    function removeArray(href, arr, params, options) {
        const url = buildUrl(href, params)
        return async function (dispatch) {
            dispatch(baseActions.deleteStart())
            try {
                const result = await http.post(url, arr, options)
                const { data } = result ?? {}
                dispatch(baseActions.deleteSuccess(data))
                if (Array.isArray(data) && arr.length !== data.length) {
                    result.error = {
                        code: 'ERR_BULK_REMOVE',
                        message: `${arr.length - data.length} of ${arr.length} elements could not be deleted!`
                    }
                }
                return result
            } catch (error) {
                dispatch(createErrorThunk(error, baseActions.deleteError(error)))
            }
        }
    }

    function assign(href, json, params) {
        return insert(`${href}/assign`, json, params)
    }

    function unassign(href, arr, params) {
        return removeArray(`${href}/unassign`, arr, params)
    }

    /**
     * Sends a patch request to the rest endpoint
     * The final URL is build by concatenating the contextPath with the href parameter
     * @param {string} href the resource href
     * @param {object} json the data in JSON format
     * @param {object} params key/value query-params pairs
     * @param {object} options additional/overwriting options for the underlying fetch method
     * @property options.abortController - an AbortController whose signal is added to the fetch options to make the request abortable
     * @return {function} a redux thunk action
     */
    function partialUpdate(href, json, params, options) {
        const url = buildUrl(href, params)
        return async function (dispatch) {
            dispatch(baseActions.updateStart())
            try {
                const result = await http.patch(url, json, options)
                const { data } = result ?? {}
                dispatch(baseActions.updateSuccess(data))
                return result
            } catch (error) {
                dispatch(createErrorThunk(error, baseActions.updateError(error)))
            }
        }
    }

    /**
     * creates a REST error dispatcher function that dispatches an action/resource-dependent action
     * additionally it dispatches a action/resource-independent error action
     * @param {Function}    dispatch    - redux dispatch method
     * @param {Function}    errorAction - function that creates a redux action for the error
     */
    function createErrorThunk(error, errorAction) {
        return dispatch => {
            logger.warn(`Crud action could not be completed: ${JSON.stringify(error)}`)
            dispatch(errorAction)
            dispatch(createNewRestErrorAction(error, resourceName))
            throw error
        }
    }

    /**
     * Builds an URL out of the provided contextPath and an href as usually embedded in REST JSON objects,
     * by concatenating both parts.
     * @param {string} href the href to be appended to the contextPath
     * @param {object|null} params key/value query-params pairs
     * @return {string} the full URL
     */
    function buildUrl(href = '', params = null) {
        let url = (contextPath + href).replace(/([^:])\/{2,}/g, '$1/')
        if (params) {
            const paramKeys = Object.keys(params)
            if (paramKeys.length > 0) {
                url +=
                    '?' +
                    Object.keys(params)
                        .map(prop => [prop, params[prop]].map(encodeURIComponent).join('='))
                        .join('&')
            }
        }
        return url
    }
}
